// The two surfaces differ ONLY in where the pixels land: the window letterboxes into a fixed box
// the map stretches to the field's projected bounds.
import { useMemo } from "react";
import { useEffect, useRef, useState } from "react";
import {
  captionFor,
  covariateIndexAt,
  covariateMs,
  flipDelayFraction,
  isScanned,
  scanOrder,
  SCAN_CELLS_PER_SEC,
  type AnimCell,
} from "../lib/mapSoilAnimation";

export const FLIP_MIN_SCALE = 0.3;
/* * The share of a covariate's window a single tile spends turning over. */
export const FLIP_WIDTH = 0.22;

export function useElapsed(running: boolean): number {
  const [t, setT] = useState(0);
  const start = useRef<number | null>(null);
  useEffect(() => {
    if (!running) {
      start.current = null;
      setT(0);
      return;
    }
    let raf = 0;
    const tick = (now: number) => {
      if (start.current == null) start.current = now;
      setT(now - start.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]);
  return t;
}

export interface TilePaint {
  /* * the viewBox height for a 100-wide box at the field's aspect */
  vh: number;
  /* * one tile's side in viewBox units */
  size: number;
  /* * every cell with the colour it shows right now and how far through its own flip it is */
  painted: { c: AnimCell; color: string; scale: number }[];
  /* * indices currently painted white by the scanner, or null when this variant has none */
  white: Set<number> | null;
  /* * the covariate name to pop up mid-map, or null before there is one */
  caption: string | null;
}

/*
 *
 * THE COLOUR SWAPS AT THE MIDPOINT OF THE TURN, and the tile is never thinner than
 * `FLIP_MIN_SCALE`: old face → thin → new face, with no frame in which the background is visible.
 */
export function useTilePaint({
  cells,
  labels,
  aspect,
  t,
  scanner,
}: {
  cells: AnimCell[];
  labels: string[];
  aspect: number;
  t: number;
  scanner: boolean;
}): TilePaint {
  const idx = covariateIndexAt(t, labels.length);
  const vh = 100 / aspect;
  const size = Math.max(
    0.6,
    (100 / Math.sqrt(Math.max(1, cells.length) * aspect)) * 1.15,
  );

  // Row-major order is DERIVED rather than assumed: on a scattered cell set "the next square in the
  // row" would otherwise jump around the field instead of crossing it.
  const order = useMemo(
    () => (scanner ? scanOrder(cells, aspect) : []),
    [scanner, cells, aspect],
  );
  const head = Math.floor((t / 1000) * SCAN_CELLS_PER_SEC);

  const painted = useMemo(() => {
    if (!cells.length || !labels.length) return [];
    const within = (t % covariateMs) / covariateMs;
    const n = labels.length;
    return cells.map((c) => {
      const delay = flipDelayFraction(c);
      const since = within - delay;
      const turning = since >= 0 && since < FLIP_WIDTH;
      const half = since >= FLIP_WIDTH / 2;
      // before its moment it still shows the PREVIOUS covariate; halfway through the turn it becomes
      // the new one. `+ n` keeps the modulo positive on the first loop.
      const at = ((since >= 0 && half ? idx : idx - 1) + n) % n;
      const scale = turning
        ? FLIP_MIN_SCALE +
          (1 - FLIP_MIN_SCALE) * Math.abs(since / FLIP_WIDTH - 0.5) * 2
        : 1;
      return { c, color: c.colors[at] ?? c.colors[0] ?? "#334155", scale };
    });
  }, [cells, labels.length, idx, t]);

  // WHITE IS AN OVERLAY, NOT A REPLACEMENT.: *"the white square should turn back to its original
  // color and the subsequent square should turn white"* — so the tile keeps its covariate colour
  // underneath and simply stops being white when the head moves on.
  const white = useMemo(() => {
    if (!scanner || !order.length) return null;
    const n = order.length;
    const set = new Set<number>();
    for (let i = 0; i < n; i++) {
      // top-left → right, wrapping to the next row DOWN
      if (isScanned(order[i]!, n, head)) {
        set.add(i);
        continue;
      }
      // bottom-right → left, wrapping to the row ABOVE — the same sweep read backwards
      if (isScanned(n - 1 - order[i]!, n, head)) set.add(i);
    }
    return set;
  }, [scanner, order, head]);

  return { vh, size, painted, white, caption: captionFor(idx, labels) };
}
